import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ExternalLink } from "lucide-react";
import { Card, PageHeader, Btn, StatusPill, Stat } from "../../components/dash/Kit";
import UsageMeter, { useUsage } from "../../components/dash/UsageMeter";
import TrialBanner from "../../components/dash/TrialBanner";
import BetaNotice from "../../components/dash/BetaNotice";
import CheckoutConsent from "../../components/dash/CheckoutConsent";
import { Link } from "react-router-dom";
import { api, errMessage } from "../../lib/api";

export default function Subscription() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [paying, setPaying] = useState("");
  const [annual, setAnnual] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [consent, setConsent] = useState({ terms: false, immediate: false });
  const consented = consent.terms && consent.immediate;
  const { usage } = useUsage();

  const load = () =>
    api
      .get("/subscription")
      .then((r) => setData(r.data))
      .catch((e) => {
        setData(false);
        setError(errMessage(e));
      });

  useEffect(() => {
    load();
  }, []);

  const checkout = async (pkg) => {
    if (!consented) {
      toast.error("Please agree to the Terms of Service and confirm immediate start before paying.");
      return;
    }
    setPaying(pkg);
    setCheckoutUrl("");
    try {
      const { data: res } = await api.post("/payments/checkout", {
        package: pkg,
        origin_url: window.location.origin,
        billing_frequency: annual ? "annual" : "monthly",
        accepted_terms: consent.terms,
        immediate_delivery_consent: consent.immediate,
      });
      setCheckoutUrl(res.checkout_url);
      // Stripe refuses to be embedded, so always hand off in a new tab.
      const opened = window.open(res.checkout_url, "_blank", "noopener");
      if (!opened) {
        toast.info("Your browser blocked the popup — use the checkout link below.");
      }
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setPaying("");
    }
  };

  const change = async (pkg) => {
    try {
      const { data: res } = await api.put("/subscription", { package: pkg });
      toast.success(res.message);
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const cancel = async () => {
    try {
      const { data: res } = await api.delete("/subscription");
      toast.success(res.message);
      setConfirmCancel(false);
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  if (data === null) return <Card testId="subscription-loading">Loading subscription…</Card>;
  if (data === false) {
    return (
      <Card testId="subscription-error">
        <p className="font-display text-lg font-semibold text-slate-900">We couldn't load your subscription</p>
        <p className="mt-2 text-sm text-slate-600">{error || "Please try again."}</p>
        <Btn className="mt-4" onClick={load} testId="subscription-retry-button">
          Try again
        </Btn>
      </Card>
    );
  }

  const { subscription, package: pkg, packages, trial } = data;
  const isFree = pkg.price === 0;
  const payable = pkg.annual_price > 0;
  const amount = annual ? pkg.annual_price : pkg.price;

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <PageHeader title="Subscription" body="Your current plan, what you've used this month and how you're billed." />

      <TrialBanner trial={trial} plan={pkg} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testId="sub-plan" label="Current Plan" value={pkg.name} />
        <Stat
          testId="sub-price"
          label="Price"
          value={isFree ? "€0" : `€${subscription.price}`}
          hint="excl. VAT where applicable"
        />
        <Stat
          testId="sub-frequency"
          label="Billing Frequency"
          value={isFree ? (trial?.on_trial && !trial.expired ? "Trial" : "—") : subscription.billing_frequency === "annual" ? "Annual" : "Monthly"}
        />
        <Stat
          testId="sub-next-billing"
          label={isFree && trial?.on_trial && !trial.expired ? "Trial Ends" : "Next Billing Date"}
          value={
            isFree
              ? trial?.ends_at
                ? new Date(trial.ends_at).toLocaleDateString()
                : "—"
              : subscription.next_billing_date
          }
        />
      </div>

      <UsageMeter
        usage={usage}
        metrics={["text_generations", "image_generations", "scheduled_posts", "connected_accounts"]}
        testId="subscription-usage-meter"
      />

      <Card testId="sub-status-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-display text-lg font-semibold text-slate-900">Subscription status</p>
          <StatusPill status={subscription.status} testId="sub-status-pill" />
        </div>
        <p className="mt-3 text-sm text-slate-600">
          Reaching an allowance never deletes anything you've made. You can upgrade whenever you want, or wait for the
          allowance to renew at the start of next month.
        </p>
      </Card>

      <Card testId="payment-card">
        <p className="font-display text-lg font-semibold text-slate-900">Pay with iDEAL or card</p>
        <div className="mt-3">
          <BetaNotice kind="payments" testId="subscription-beta-notice" />
        </div>

        {isFree ? (
          <p className="mt-2 text-sm text-slate-600">
            {trial?.expired
              ? "Your trial has ended, so there's nothing on file to bill. Choose Starter, Grow or Pro below to switch everything back on."
              : "You're on the free trial, so nothing has been charged yet. When the trial ends your plan starts automatically and your card is charged — we email you first. Cancel before then and you pay nothing."}
          </p>
        ) : (
          <>
            <p className="mt-2 text-sm text-slate-600">
              {subscription.payment_provider === "stripe" && subscription.status === "active"
                ? "Your subscription is active. You can switch plans or billing frequency at any time."
                : "Activate billing for your plan. Checkout opens in a new tab where you can pay with iDEAL, credit card or debit card."}
            </p>

            <div className="mt-4 inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1">
              {[
                ["monthly", "Monthly"],
                ["annual", "Annual — 2 months free"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setAnnual(key === "annual")}
                  data-testid={`sub-frequency-${key}`}
                  aria-pressed={(key === "annual") === annual}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    (key === "annual") === annual ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <CheckoutConsent
              plan={pkg}
              annual={annual}
              amount={amount}
              consent={consent}
              setConsent={setConsent}
            />

            <Btn
              testId="pay-now-button"
              className="mt-4"
              disabled={!!paying || !payable || !consented}
              onClick={() => checkout(pkg.id)}
            >
              {paying
                ? "Opening checkout…"
                : `Subscribe & pay €${amount} ${annual ? "per year" : "per month"} — starts a recurring payment`}
            </Btn>

            {checkoutUrl && (
              <a
                href={checkoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="checkout-link"
                className="mt-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-[#FAFAFA] px-4 py-3 text-sm font-semibold text-slate-800 hover:border-slate-400"
              >
                <ExternalLink className="h-4 w-4 shrink-0" aria-hidden="true" />
                Checkout didn't open? Open your secure Stripe checkout page
              </a>
            )}
          </>
        )}

        <p className="mt-3 text-xs text-slate-500">
          Prices exclude VAT where legally applicable. Tax is calculated at checkout. You will never be charged extra
          without clear authorisation. See our{" "}
          <Link to="/legal/refund-policy" className="font-semibold text-[#F95738] hover:underline">
            Refund &amp; Cancellation Policy
          </Link>
          .
        </p>
      </Card>

      <div>
        <p className="mb-3 font-display text-lg font-semibold text-slate-900">Change your plan</p>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="plan-options">
          {packages.map((p) => (
            <Card key={p.id} testId={`plan-option-${p.id}`} className={p.id === pkg.id ? "border-[#F95738]" : ""}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-display text-xl font-bold text-slate-900">{p.name}</p>
                {p.id === pkg.id && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#FFF3EF] px-2.5 py-1 text-xs font-semibold text-[#B93615]">
                    <Check className="h-3 w-3" /> Current
                  </span>
                )}
              </div>
              <p className="mt-2 font-display text-2xl font-bold text-slate-900">
                €{p.price}
                <span className="text-sm font-medium text-slate-500">/mo</span>
              </p>
              <p className="mt-3 text-sm text-slate-600">
                {p.limits.text_generations} AI generations · {p.limits.scheduled_posts} scheduled posts ·{" "}
                {p.limits.connected_accounts} accounts
              </p>
              {p.id !== pkg.id && (
                <div className="mt-5 space-y-2">
                  <Btn
                    testId={`plan-select-${p.id}`}
                    variant={p.price > pkg.price ? "primary" : "outline"}
                    className="w-full"
                    onClick={() => change(p.id)}
                  >
                    {p.price > pkg.price ? "Upgrade Plan" : "Downgrade Plan"}
                  </Btn>
                  {p.price > 0 && (
                    <Btn
                      variant="ghost"
                      testId={`plan-pay-${p.id}`}
                      className="w-full"
                      disabled={!!paying || !consented}
                      onClick={() => checkout(p.id)}
                    >
                      Switch &amp; pay now
                    </Btn>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>

      <Card testId="cancel-card">
        <p className="font-display text-lg font-semibold text-slate-900">Cancel subscription</p>
        <p className="mt-2 text-sm text-slate-600">
          Cancel here at any time — no phone call, no retention maze. Cancellation takes effect at the end of the period
          you have already paid for, and you keep access until then. Afterwards, generating, scheduling and publishing
          stop; your content stays in your account until you delete it.
        </p>
        <p className="mt-2 text-sm">
          <Link to="/legal/refund-policy" data-testid="cancel-policy-link" className="font-semibold text-[#F95738] hover:underline">
            Read the Refund &amp; Cancellation Policy
          </Link>
        </p>
        {confirmCancel ? (
          <div className="mt-4 flex flex-wrap gap-2">
            <Btn variant="danger" testId="cancel-confirm-button" onClick={cancel}>
              Yes, cancel my subscription
            </Btn>
            <Btn variant="outline" testId="cancel-abort-button" onClick={() => setConfirmCancel(false)}>
              Keep my plan
            </Btn>
          </div>
        ) : (
          <Btn
            variant="danger"
            testId="cancel-subscription-button"
            className="mt-4"
            onClick={() => setConfirmCancel(true)}
            disabled={subscription.status === "cancelled"}
          >
            {subscription.status === "cancelled" ? "Already cancelled" : "Cancel Subscription"}
          </Btn>
        )}
      </Card>
    </div>
  );
}
